import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, ChevronLeft, Trash2 } from 'lucide-react';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { SelectField } from '@/features/clients/SelectField';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { StatusBadge } from '@/components/ui/Badge';
import { useAuthStore } from '@/stores/auth';
import { useClientsList } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import {
    useActiveSessionTypes,
    useActiveLocationTypes,
    useActiveCancelReasons,
} from '@/api/referenceData';
import { useSettings } from '@/api/settings';
import {
    useUpdateSession,
    useDeleteSession,
    useUpdateSeries,
    useDeleteSeries,
    useUpdateSeriesEndDate,
    usePromoteToSeries,
    type SessionRecord,
    type SessionSeries,
    type SeriesUpdatePayload,
} from '@/api/sessions';
import type { SessionDetail } from '@/api/notes';
import {
    SESSION_STATUSES,
    generateSeriesDates,
    toIsoDate,
    type RecurrenceSpec,
    type SessionStatus,
} from '@/lib/calendar';
import { checkAvailability } from '@/lib/availability';
import { cn } from '@/lib/cn';

/*
 * SessionEditForm — edit/delete/promote UI for an existing session.
 *
 * Lives inside the Detail tab on SessionPage. Unlike the create-only
 * SessionDialog, this form operates on a real SessionDetail row and can:
 *   - PATCH a single occurrence
 *   - PATCH a whole series (This & following) incl. end-date shifts
 *   - DELETE single / following
 *   - Promote a one-off to a series (new recurring_series + back-fill dates)
 *
 * Promote is one-way; once a session lives in a series the recurring
 * checkbox locks on and the user can only modify scope-aware fields.
 */

export type SessionEditScope = 'this' | 'following';

const STATUS_MENU_OPTIONS: SessionStatus[] = [
    'Scheduled',
    'Complete',
    'Canceled',
];

const WEEKDAY_TOGGLES: { value: number; short: string }[] = [
    { value: 0, short: 'Sun' },
    { value: 1, short: 'Mon' },
    { value: 2, short: 'Tue' },
    { value: 3, short: 'Wed' },
    { value: 4, short: 'Thu' },
    { value: 5, short: 'Fri' },
    { value: 6, short: 'Sat' },
];

const schema = z.object({
    // Admin session types (trainings, meetings) don't have a client, so the
    // schema can't require it here. The component enforces "required unless
    // admin" in the submit handler using the selected type's category.
    clientId:          z.string().optional(),
    staffId:           z.string().min(1, 'Staff is required'),
    serviceTypeId:     z.string().min(1, 'Session type is required'),
    date:              z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date is required'),
    startTime:         z.string().regex(/^\d{2}:\d{2}$/, 'Start time is required'),
    endTime:           z.string().regex(/^\d{2}:\d{2}$/, 'End time is required'),
    location:          z.string().optional(),
    label:             z.string().optional(),
    notes:             z.string().optional(),
    status:            z.enum(SESSION_STATUSES),
    cancelReasonId:    z.string().optional(),
    isSupervised:      z.boolean(),
    supervisingBcbaId: z.string().optional(),

    recurring:         z.boolean(),
    recurrenceType:    z.enum(['weekly', 'daily']),
    weekInterval:      z.number().int().min(1).max(12),
    daysOfWeek:        z.array(z.number().int().min(0).max(6)),
    recurrenceEndDate: z.string().optional(),
})
    .refine(
        (v) => timeToMinutes(v.endTime) > timeToMinutes(v.startTime),
        { message: 'End time must be after start time.', path: ['endTime'] },
    )
    .refine(
        (v) => !isCanceledStatus(v.status) || !!v.cancelReasonId,
        { message: 'Reason is required when canceling.', path: ['cancelReasonId'] },
    )
    .refine(
        (v) => !v.isSupervised || !!v.supervisingBcbaId,
        { message: 'Supervising BCBA is required when supervised.', path: ['supervisingBcbaId'] },
    )
    .refine(
        (v) => !v.recurring || (v.recurrenceEndDate && /^\d{4}-\d{2}-\d{2}$/.test(v.recurrenceEndDate)),
        { message: 'End date is required for recurring sessions.', path: ['recurrenceEndDate'] },
    )
    .refine(
        (v) => !v.recurring || !v.recurrenceEndDate || v.recurrenceEndDate >= v.date,
        { message: 'End date must be on or after the start date.', path: ['recurrenceEndDate'] },
    )
    .refine(
        (v) => !v.recurring || v.recurrenceType !== 'daily' || v.daysOfWeek.length > 0,
        { message: 'Pick at least one day.', path: ['daysOfWeek'] },
    );

type FormValues = z.infer<typeof schema>;

function isCanceledStatus(s: string): boolean {
    return s === 'Canceled';
}

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

function formatTime12(hhmm: string): string {
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return '';
    const suffix = h >= 12 ? 'pm' : 'am';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${pad(m)} ${suffix}`;
}

function timeToMinutes(t: string): number {
    const [h, m] = t.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}

function minutesToTime(total: number): string {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${pad(h)}:${pad(m)}`;
}

function sessionDateMinIso(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 2);
    return toIsoDate(d);
}

function sessionDateMaxIso(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return toIsoDate(d);
}

function formatDuration(minutes: number): string {
    if (minutes <= 0) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const parts: string[] = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    return parts.join(' ');
}

const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SHORT_DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function weekdayFromIso(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return null;
    return WEEKDAY_LONG[new Date(y, m - 1, d).getDay()];
}

function formatDateShort(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return null;
    return `${m}/${d}/${String(y).slice(-2)}`;
}

export function describeRecurrence(
    series: SessionSeries | null | undefined,
    sessionDate?: string | null,
): string {
    if (!series) return 'One-time';
    const endShort = formatDateShort(series.endDate);
    const until = endShort ? ` until ${endShort}` : '';

    if (series.patternType === 'weekly') {
        const n = series.weekInterval ?? 1;
        const step = n === 1 ? 'Every week' : `Every ${n} weeks`;
        const weekday = weekdayFromIso(sessionDate);
        const onDay = weekday ? ` on ${weekday}` : '';
        return `${step}${onDay}${until}`;
    }

    const days = series.daysOfWeek ?? [];
    if (days.length === 7) return `Every day${until}`;
    const names = [...days]
        .filter((d) => d >= 0 && d <= 6)
        .sort((a, b) => a - b)
        .map((d) => SHORT_DAY[d])
        .join(', ');
    return `Every week on ${names}${until}`;
}

export interface SessionEditFormProps {
    session: SessionDetail;
    onCancel: () => void;
    onSaved?: () => void;
    onDeleted?: () => void;
}

export function SessionEditForm({
    session, onCancel, onSaved, onDeleted,
}: SessionEditFormProps) {
    const canDelete = useAuthStore((s) => s.hasPermission('delete_sessions'));

    const clients = useClientsList();
    const staff = useUserDirectory();
    // BCBAs derived from the full directory — the entries already carry
    // roleType, so a separate filtered query duplicates work TanStack Query
    // can't dedupe across keys.
    const bcbas = useMemo(
        () => (staff.data ?? []).filter((u) => u.roleType === 'BCBA'),
        [staff.data],
    );
    const sessionTypes = useActiveSessionTypes();
    const locations = useActiveLocationTypes();
    const cancelReasons = useActiveCancelReasons();
    const settings = useSettings();
    const queryClient = useQueryClient();

    const updateMut = useUpdateSession();
    const deleteMut = useDeleteSession();
    const updateSeriesMut = useUpdateSeries();
    const deleteSeriesMut = useDeleteSeries();
    const updateSeriesEndDateMut = useUpdateSeriesEndDate();
    const promoteMut = usePromoteToSeries();

    const isSeries = !!session.seriesId;
    const canPromote = !isSeries && session.status !== 'Finalized' && !!session.date;

    const [scope, setScope] = useState<SessionEditScope>('this');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [cancelTypePending, setCancelTypePending] = useState<SessionStatus | null>(null);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);

    /*
     * Active clients + fallback for deactivated — keep the current client's
     * row visible even after deactivation so the select doesn't render empty
     * on legacy sessions.
     */
    const activeClients = useMemo(() => {
        const all = clients.data ?? [];
        const filtered = all.filter((c) => c.status === 'Active');
        if (session.clientId) {
            const already = filtered.some((c) => c.id === session.clientId);
            if (!already) {
                const existing = all.find((c) => c.id === session.clientId);
                if (existing) filtered.push(existing);
            }
        }
        return filtered;
    }, [clients.data, session.clientId]);

    const defaults = useMemo<FormValues>(() => {
        const end = session.startH * 60 + session.startM + session.dur;
        const s = session.series;
        return {
            clientId:          session.clientId ?? '',
            staffId:           session.staffId ?? '',
            serviceTypeId:     session.serviceTypeId ?? '',
            date:              session.date ?? '',
            startTime:         `${pad(session.startH)}:${pad(session.startM)}`,
            endTime:           minutesToTime(end),
            location:          session.location ?? '',
            label:             session.label ?? '',
            notes:             session.notes ?? '',
            status:            (SESSION_STATUSES.includes(session.status as SessionStatus)
                ? session.status
                : 'Scheduled') as SessionStatus,
            cancelReasonId:    session.cancelReasonId ?? '',
            isSupervised:      !!session.isSupervised,
            supervisingBcbaId: session.supervisingBcbaId ?? '',
            recurring:         isSeries,
            recurrenceType:    s?.patternType ?? 'weekly',
            weekInterval:      s?.weekInterval ?? 1,
            daysOfWeek:        s?.daysOfWeek ?? [],
            recurrenceEndDate: s?.endDate ?? '',
        };
    }, [session, isSeries]);

    const {
        register, handleSubmit, reset, watch, setValue, setError, control,
        formState: { errors, isSubmitting, isDirty },
    } = useForm<FormValues>({
        resolver: zodResolver(schema) as Resolver<FormValues>,
        defaultValues: defaults,
    });

    useEffect(() => {
        reset(defaults);
    }, [defaults, reset]);

    const clientId          = watch('clientId');
    const staffId           = watch('staffId');
    const selectedType      = watch('serviceTypeId');
    const isSupervised      = watch('isSupervised');
    const supervisingBcbaId = watch('supervisingBcbaId');
    const labelValue        = watch('label');
    const startTime         = watch('startTime');
    const endTime           = watch('endTime');
    const status            = watch('status');
    const recurring         = watch('recurring');
    const recurrenceType    = watch('recurrenceType');
    const weekInterval      = watch('weekInterval');
    const daysOfWeek        = watch('daysOfWeek');
    const dateValue         = watch('date');
    const recurrenceEndDate = watch('recurrenceEndDate');

    /*
     * Auto-fill label on type change only when empty. Respects user edits.
     */
    useEffect(() => {
        if (!selectedType) return;
        const t = sessionTypes.data?.find((st) => st.id === selectedType);
        if (t && !labelValue) {
            setValue('label', t.labelDefault || t.name, { shouldDirty: false });
        }
    }, [selectedType, sessionTypes.data, labelValue, setValue]);

    /*
     * Admin session types (trainings, meetings) aren't tied to a client or a
     * location. When the user switches to one, clear those fields so stale
     * values don't get saved.
     */
    const isAdminType = useMemo(() => {
        const t = sessionTypes.data?.find((st) => st.id === selectedType);
        return t?.category === 'Administrative';
    }, [selectedType, sessionTypes.data]);

    useEffect(() => {
        if (isAdminType) {
            setValue('clientId', '', { shouldDirty: true });
            setValue('location', '', { shouldDirty: true });
        }
    }, [isAdminType, setValue]);

    const isCanceling = isCanceledStatus(status);

    // Promotion (one-off → series) — only meaningful when the user just
    // checked the recurring box on a previously one-off session.
    const isPromoting = !isSeries && recurring;

    const spec: RecurrenceSpec = useMemo(
        () =>
            recurrenceType === 'weekly'
                ? { type: 'weekly', weekInterval: Math.max(1, Math.floor(weekInterval || 1)) }
                : { type: 'daily', daysOfWeek: daysOfWeek ?? [] },
        [recurrenceType, weekInterval, daysOfWeek],
    );

    const occurrences = useMemo(() => {
        if (!isPromoting) return [];
        if (!dateValue || !recurrenceEndDate) return [];
        return generateSeriesDates(dateValue, recurrenceEndDate, spec);
    }, [isPromoting, dateValue, recurrenceEndDate, spec]);

    const durMinutes = useMemo(
        () => Math.max(0, timeToMinutes(endTime || '00:00') - timeToMinutes(startTime || '00:00')),
        [startTime, endTime],
    );
    const seriesHours = (occurrences.length * durMinutes) / 60;

    const selectedClient = useMemo(
        () => activeClients.find((c) => c.id === clientId),
        [activeClients, clientId],
    );
    const remainingHours = selectedClient?.auth
        ? Math.max(0, selectedClient.auth.hours - selectedClient.auth.used)
        : null;
    const exceedsAuth = isPromoting && remainingHours !== null && seriesHours > remainingHours;
    const authBlock = settings.data?.billing?.authBlock ?? 'soft';
    const hardBlock = exceedsAuth && authBlock === 'hard';

    // Track when any sessions query last updated so the conflict memo
    // re-runs on new data rather than staying frozen at mount time.
    const [sessionsTick, setSessionsTick] = useState(0);
    useEffect(() => {
        return queryClient.getQueryCache().subscribe((event) => {
            if (event.query.queryKey[0] === 'sessions' && event.type === 'updated') {
                setSessionsTick((t) => t + 1);
            }
        });
    }, [queryClient]);
    const allCachedSessions = useMemo(() => {
        void sessionsTick; // reactive dependency
        const buckets = queryClient.getQueriesData<SessionRecord[]>({ queryKey: ['sessions'] });
        const seen = new Map<string, SessionRecord>();
        for (const [, rows] of buckets) {
            if (!rows) continue;
            for (const row of rows) seen.set(row.id, row);
        }
        return Array.from(seen.values());
    }, [queryClient, sessionsTick]);

    const conflictWarnings = useMemo<string[]>(() => {
        if (!dateValue || !startTime || !endTime) return [];
        const newStart = timeToMinutes(startTime);
        const newEnd   = timeToMinutes(endTime);
        if (newEnd <= newStart) return [];

        const candidates = allCachedSessions.filter((s) => {
            if (!s.date || s.date !== dateValue) return false;
            if (s.id === session.id) return false;
            if (s.status === 'Canceled') return false;
            const sStart = s.startH * 60 + s.startM;
            const sEnd   = sStart + s.dur;
            return newStart < sEnd && sStart < newEnd;
        });

        const msgs: string[] = [];
        if (staffId) {
            const hit = candidates.find((s) => s.staffId === staffId);
            if (hit) {
                const at = formatTime12(`${pad(hit.startH)}:${pad(hit.startM)}`);
                msgs.push(`Staff already has a session at ${at} with ${hit.client || 'another client'}.`);
            }
        }
        if (clientId) {
            const hit = candidates.find((s) => s.clientId === clientId);
            if (hit) {
                const at = formatTime12(`${pad(hit.startH)}:${pad(hit.startM)}`);
                msgs.push(`Client already has a session at ${at} with ${hit.staff || 'another staff member'}.`);
            }
        }

        const window = `${formatTime12(startTime)}–${formatTime12(endTime)}`;

        if (staffId) {
            const u = staff.data?.find((x) => x.id === staffId);
            const check = checkAvailability(u?.availability, dateValue, startTime, endTime);
            if (check.kind === 'outside') {
                const name = u ? `${u.first} ${u.last}` : 'Staff';
                msgs.push(`${name} is unavailable ${check.dayLabel} from ${window}.`);
            }
        }
        if (clientId && selectedClient) {
            const check = checkAvailability(selectedClient.availability, dateValue, startTime, endTime);
            if (check.kind === 'outside') {
                const name = `${selectedClient.first} ${selectedClient.last}`;
                msgs.push(`${name} is unavailable ${check.dayLabel} from ${window}.`);
            }
        }
        if (isSupervised && supervisingBcbaId) {
            const u = bcbas.find((x) => x.id === supervisingBcbaId);
            const check = checkAvailability(u?.availability, dateValue, startTime, endTime);
            if (check.kind === 'outside') {
                const name = u ? `${u.first} ${u.last}` : 'Supervising BCBA';
                msgs.push(`${name} is unavailable ${check.dayLabel} from ${window}.`);
            }
        }
        return msgs;
    }, [
        dateValue, startTime, endTime, staffId, clientId, allCachedSessions, session.id,
        isSupervised, supervisingBcbaId, staff.data, bcbas, selectedClient,
    ]);

    const finalizedInRangeCount = useMemo<number>(() => {
        if (!isSeries || scope !== 'following' || !session.date) return 0;
        return allCachedSessions.filter(
            (s) =>
                s.seriesId === session.seriesId
                && s.date
                && s.date >= session.date!
                && s.status === 'Finalized',
        ).length;
    }, [isSeries, scope, session, allCachedSessions]);

    async function onSubmit(values: FormValues) {
        if (hardBlock) return;
        if (!isAdminType && !values.clientId) {
            setError('clientId', { type: 'manual', message: 'Client is required' });
            return;
        }
        const [sh, sm] = values.startTime.split(':').map(Number);
        const dur = Math.max(15, timeToMinutes(values.endTime) - timeToMinutes(values.startTime));
        const labelOut = values.label || '';
        const locationOut = values.location || '';
        const notesOut = values.notes || '';
        const supBcba = values.isSupervised ? (values.supervisingBcbaId || null) : null;

        try {
            if (isPromoting) {
                // One-off → series promotion. Persist any scalar edits on
                // the row first so the promote endpoint's back-fill uses
                // the latest values, then POST the new series. Saving
                // twice is fine; the server audits each.
                if (isDirty) {
                    await updateMut.mutateAsync({
                        id: session.id,
                        payload: {
                            clientId:          values.clientId,
                            staffId:           values.staffId,
                            serviceTypeId:     values.serviceTypeId,
                            date:              values.date,
                            startH:            sh,
                            startM:            sm,
                            dur,
                            label:             labelOut,
                            location:          locationOut,
                            notes:             notesOut,
                            status:            values.status as SessionStatus,
                            isSupervised:      values.isSupervised,
                            supervisingBcbaId: supBcba,
                            cancelReasonId:    isCanceledStatus(values.status) ? (values.cancelReasonId || null) : null,
                        },
                    });
                }
                if (occurrences.length === 0) return;
                await promoteMut.mutateAsync({
                    id: session.id,
                    payload: {
                        patternType:  spec.type,
                        weekInterval: spec.type === 'weekly' ? spec.weekInterval : undefined,
                        daysOfWeek:   spec.type === 'daily' ? spec.daysOfWeek : undefined,
                        dates:        occurrences,
                    },
                });
            } else if (isSeries && scope === 'following') {
                // Bulk series edit. End-date first so extend's back-fill
                // uses pre-edit values before the field patch sweeps the
                // (possibly longer) series.
                const originalEnd = session.series?.endDate ?? '';
                const newEnd = values.recurrenceEndDate ?? '';
                if (newEnd && newEnd !== originalEnd) {
                    await updateSeriesEndDateMut.mutateAsync({
                        seriesId: session.seriesId!,
                        endDate: newEnd,
                    });
                }
                const payload: SeriesUpdatePayload = {
                    staffId:           values.staffId,
                    serviceTypeId:     values.serviceTypeId,
                    startH:            sh,
                    startM:            sm,
                    dur,
                    label:             labelOut,
                    location:          locationOut,
                    notes:             notesOut,
                    status:            values.status as SessionStatus,
                    isSupervised:      values.isSupervised,
                    supervisingBcbaId: supBcba,
                    cancelReasonId:    isCanceledStatus(values.status) ? (values.cancelReasonId || null) : null,
                };
                await updateSeriesMut.mutateAsync({
                    seriesId: session.seriesId!,
                    fromDate: session.date!,
                    payload,
                });
            } else {
                await updateMut.mutateAsync({
                    id: session.id,
                    payload: {
                        clientId:          values.clientId,
                        staffId:           values.staffId,
                        serviceTypeId:     values.serviceTypeId,
                        date:              values.date,
                        startH:            sh,
                        startM:            sm,
                        dur,
                        label:             labelOut,
                        location:          locationOut,
                        notes:             notesOut,
                        status:            values.status as SessionStatus,
                        isSupervised:      values.isSupervised,
                        supervisingBcbaId: supBcba,
                        cancelReasonId:    isCanceledStatus(values.status) ? (values.cancelReasonId || null) : null,
                    },
                });
            }
            onSaved?.();
        } catch {
            /* surfaced below via mutation error */
        }
    }

    async function onDelete() {
        try {
            if (isSeries && scope === 'following') {
                await deleteSeriesMut.mutateAsync({
                    seriesId: session.seriesId!,
                    fromDate: session.date!,
                });
            } else {
                await deleteMut.mutateAsync(session.id);
            }
            onDeleted?.();
        } catch {
            /* surfaced below via mutation error */
        }
    }

    const serverError = (() => {
        const e = updateMut.error ?? deleteMut.error
            ?? updateSeriesMut.error ?? deleteSeriesMut.error
            ?? updateSeriesEndDateMut.error ?? promoteMut.error;
        if (!e) return null;
        if (e instanceof AxiosError) {
            return e.response?.data?.error ?? 'Failed to save session';
        }
        return 'Unexpected error';
    })();

    const deletePending = deleteMut.isPending || deleteSeriesMut.isPending;

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            /*
             * Fills its parent Card (which passes `p-0 overflow-hidden
             * flex flex-col` so the form manages its own three regions).
             * Header and footer stay put; only the middle scrolls.
             */
            className="flex flex-col h-full min-h-0 bg-surface"
        >
            <div className="shrink-0 px-5 py-3 border-b border-border flex flex-wrap items-center gap-x-5 gap-y-2">
                <div className="flex items-center gap-2">
                    <DropdownMenu.Root
                        open={statusMenuOpen}
                        onOpenChange={(o) => {
                            setStatusMenuOpen(o);
                            if (!o) setCancelTypePending(null);
                        }}
                    >
                        <DropdownMenu.Trigger asChild>
                            <button
                                type="button"
                                className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                aria-label="Change status"
                                title="Change status"
                            >
                                <StatusBadge
                                    status={status}
                                    trailingIcon={<ChevronDown className="w-3 h-3 -mr-0.5 opacity-70" />}
                                    className="h-7 px-2.5 cursor-pointer ring-1 ring-transparent hover:ring-border hover:brightness-95"
                                />
                            </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content
                                align="start"
                                sideOffset={6}
                                className="min-w-[200px] bg-surface border border-border rounded-md shadow-md py-1 z-[60]"
                            >
                                {cancelTypePending === null && STATUS_MENU_OPTIONS.map((opt) => {
                                    const isCancel = isCanceledStatus(opt);
                                    return (
                                        <DropdownMenu.Item
                                            key={opt}
                                            onSelect={(e) => {
                                                if (isCancel) {
                                                    e.preventDefault();
                                                    setCancelTypePending(opt);
                                                    return;
                                                }
                                                setValue('status', opt, { shouldDirty: true, shouldValidate: true });
                                                setValue('cancelReasonId', '', { shouldDirty: true });
                                            }}
                                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-text hover:bg-surface-muted cursor-pointer outline-none"
                                        >
                                            <StatusBadge status={opt} className="h-5 px-1.5 text-[10px]" />
                                            {isCancel && (
                                                <ChevronDown className="w-3 h-3 ml-auto -rotate-90 text-text-subtle" />
                                            )}
                                        </DropdownMenu.Item>
                                    );
                                })}

                                {cancelTypePending !== null && (
                                    <>
                                        <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border mb-1">
                                            <button
                                                type="button"
                                                onClick={() => setCancelTypePending(null)}
                                                className="p-0.5 rounded hover:bg-surface-muted text-text-muted"
                                                aria-label="Back to statuses"
                                            >
                                                <ChevronLeft className="w-3.5 h-3.5" />
                                            </button>
                                            <span className="text-2xs font-bold uppercase tracking-wider text-text-subtle">
                                                Reason
                                            </span>
                                            <StatusBadge status={cancelTypePending} className="h-5 px-1.5 text-[10px] ml-auto" />
                                        </div>
                                        {(cancelReasons.data ?? []).length === 0 && (
                                            <div className="px-3 py-2 text-xs text-text-subtle">
                                                No cancel reasons configured.
                                            </div>
                                        )}
                                        {(cancelReasons.data ?? []).map((r) => (
                                            <DropdownMenu.Item
                                                key={r.id}
                                                onSelect={() => {
                                                    const chosen = cancelTypePending;
                                                    if (!chosen) return;
                                                    setValue('status', chosen, { shouldDirty: true, shouldValidate: true });
                                                    setValue('cancelReasonId', r.id, { shouldDirty: true, shouldValidate: true });
                                                    setCancelTypePending(null);
                                                }}
                                                className="px-3 py-1.5 text-sm text-text hover:bg-surface-muted cursor-pointer outline-none"
                                            >
                                                {r.text}
                                            </DropdownMenu.Item>
                                        ))}
                                    </>
                                )}
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                </div>

                {isSeries && (
                    <fieldset className="flex flex-wrap items-center gap-4 text-sm">
                        <legend className="sr-only">Edit scope</legend>
                        <span className="text-text-muted text-xs uppercase tracking-wider">Edits apply to:</span>
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                name="scope"
                                checked={scope === 'this'}
                                onChange={() => setScope('this')}
                            />
                            This occurrence
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                name="scope"
                                checked={scope === 'following'}
                                onChange={() => setScope('following')}
                            />
                            This &amp; all following
                        </label>
                    </fieldset>
                )}
            </div>

            <div className="flex-1 min-h-0 overflow-auto px-5 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-x-3 gap-y-0">
                <div className="col-span-2 mb-3">
                    <span className="text-2xs font-bold uppercase tracking-wider text-text-subtle">Details</span>
                </div>

                <FormField
                    label="Session type"
                    required
                    error={errors.serviceTypeId?.message}
                    className="col-span-2"
                >
                    {(id) => (
                        <SelectField id={id} {...register('serviceTypeId')}>
                            <option value="">Select type…</option>
                            {(sessionTypes.data ?? []).map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </SelectField>
                    )}
                </FormField>

                {!isAdminType && (
                    <>
                        <FormField label="Client">
                            {(id) => {
                                const c = activeClients.find((x) => x.id === clientId);
                                return (
                                    <div
                                        id={id}
                                        className="w-full px-3 py-2 bg-surface-muted border border-border rounded-md text-sm text-text-subtle cursor-not-allowed"
                                    >
                                        {c ? `${c.first} ${c.last}` : '—'}
                                    </div>
                                );
                            }}
                        </FormField>

                        <FormField label="Location" error={errors.location?.message}>
                            {(id) => (
                                <SelectField id={id} {...register('location')}>
                                    <option value="">—</option>
                                    {(locations.data ?? []).map((l) => (
                                        <option key={l.id} value={l.name}>{l.name}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                    </>
                )}

                <FormField label="Staff" required error={errors.staffId?.message}>
                    {(id) => (
                        <SelectField id={id} {...register('staffId')}>
                            <option value="">Select staff…</option>
                            {(staff.data ?? []).map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.first} {u.last} · {u.roleName}
                                </option>
                            ))}
                        </SelectField>
                    )}
                </FormField>

                <FormField
                    label="Label"
                    hint={!labelValue && selectedType ? 'Defaults to session type name.' : undefined}
                    error={errors.label?.message}
                >
                    {(id) => <Input id={id} {...register('label')} />}
                </FormField>

                <div className="col-span-2 mb-3 mt-1 pt-3 border-t border-border">
                    <span className="text-2xs font-bold uppercase tracking-wider text-text-subtle">Schedule</span>
                </div>

                <FormField label="Date" required error={errors.date?.message}>
                    {(id) => (
                        <Input
                            id={id}
                            type="date"
                            min={sessionDateMinIso()}
                            max={sessionDateMaxIso()}
                            // Series bulk edit doesn't accept date changes
                            // (rewriting dates across a series scrambles
                            // occurrence spacing). This-occurrence and
                            // one-off promotion both do.
                            disabled={isSeries && scope === 'following'}
                            {...register('date')}
                        />
                    )}
                </FormField>
                <div />

                <FormField label="Start time" required error={errors.startTime?.message}>
                    {(id) => (
                        <Input id={id} type="time" step={900} {...register('startTime')} />
                    )}
                </FormField>

                <FormField
                    label="End time"
                    required
                    error={errors.endTime?.message}
                    hint={formatDuration(durMinutes) ? `Duration: ${formatDuration(durMinutes)}` : undefined}
                >
                    {(id) => (
                        <Input id={id} type="time" step={900} {...register('endTime')} />
                    )}
                </FormField>

                {/*
                 * Recurrence row.
                 *   Existing series: read-only cadence sentence; end-date is the
                 *     only editable part of the pattern and only when scope is
                 *     "following".
                 *   One-off: checkbox enables the promote-to-series flow —
                 *     pattern builder + end-date, identical to create.
                 */}
                {isSeries && session.series && (
                    <>
                        <div className="col-span-2 mb-3.5 text-xs text-text-subtle">
                            Repeats{' '}
                            <span className="text-text">{describeRecurrence(session.series, session.date)}</span>.
                            {scope !== 'following' && (
                                <>
                                    {' '}Switch to{' '}
                                    <span className="text-text">&quot;This &amp; all following&quot;</span>
                                    {' '}above to change the end date.
                                </>
                            )}
                        </div>

                        {scope === 'following' && (
                            <FormField
                                label="Series ends"
                                required
                                error={errors.recurrenceEndDate?.message}
                                hint="Changing this extends or trims the series."
                            >
                                {(id) => (
                                    <Input id={id} type="date" min={dateValue || sessionDateMinIso()} max={sessionDateMaxIso()} {...register('recurrenceEndDate')} />
                                )}
                            </FormField>
                        )}
                    </>
                )}

                {canPromote && (
                    <>
                        <div className="col-span-1 mb-3.5 flex items-center">
                            <label className="flex items-center gap-2 text-sm text-text">
                                <input type="checkbox" {...register('recurring')} />
                                Make recurring
                            </label>
                        </div>
                        <div />

                        {isPromoting && (
                            <>
                                <FormField label="Recurrence" required>
                                    {(id) => (
                                        <SelectField id={id} {...register('recurrenceType')}>
                                            <option value="weekly">Weekly</option>
                                            <option value="daily">Daily</option>
                                        </SelectField>
                                    )}
                                </FormField>

                                {recurrenceType === 'weekly' ? (
                                    <FormField label="Every (weeks)" error={errors.weekInterval?.message}>
                                        {(id) => (
                                            <Input
                                                id={id}
                                                type="number"
                                                step={1}
                                                min={1}
                                                max={12}
                                                {...register('weekInterval', { valueAsNumber: true })}
                                            />
                                        )}
                                    </FormField>
                                ) : (
                                    <FormField label="Days of week" error={errors.daysOfWeek?.message as string | undefined}>
                                        {() => (
                                            <Controller
                                                control={control}
                                                name="daysOfWeek"
                                                render={({ field }) => (
                                                    <div className="flex flex-wrap gap-1">
                                                        {WEEKDAY_TOGGLES.map((d) => {
                                                            const checked = field.value?.includes(d.value);
                                                            return (
                                                                <button
                                                                    key={d.value}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const next = checked
                                                                            ? (field.value ?? []).filter((v) => v !== d.value)
                                                                            : [...(field.value ?? []), d.value];
                                                                        field.onChange(next);
                                                                    }}
                                                                    className={cn(
                                                                        'px-2 py-1 rounded-md text-xs border transition-colors',
                                                                        checked
                                                                            ? 'bg-accent text-[#111] border-accent'
                                                                            : 'bg-background text-text-muted border-border hover:text-text',
                                                                    )}
                                                                >
                                                                    {d.short}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            />
                                        )}
                                    </FormField>
                                )}

                                <FormField label="Starts" hint="Uses the Date field above.">
                                    {(id) => (
                                        <Input id={id} type="date" value={dateValue} disabled readOnly />
                                    )}
                                </FormField>
                                <FormField
                                    label="Ends"
                                    required
                                    error={errors.recurrenceEndDate?.message}
                                >
                                    {(id) => (
                                        <Input id={id} type="date" min={dateValue || sessionDateMinIso()} max={sessionDateMaxIso()} {...register('recurrenceEndDate')} />
                                    )}
                                </FormField>
                            </>
                        )}
                    </>
                )}

                {(isPromoting || exceedsAuth || conflictWarnings.length > 0 || finalizedInRangeCount > 0) && (
                    <div className="col-span-2 mb-3.5 space-y-2">
                        {isPromoting && (
                            <Alert variant="info">
                                {occurrences.length === 0
                                    ? 'No occurrences match this pattern + date range.'
                                    : `${occurrences.length} occurrence${occurrences.length === 1 ? '' : 's'} · ${seriesHours.toFixed(2)} total hours.`}
                            </Alert>
                        )}
                        {exceedsAuth && (
                            <Alert variant={hardBlock ? 'danger' : 'warning'}>
                                {hardBlock
                                    ? `Series ${seriesHours.toFixed(2)}h exceeds remaining auth (${remainingHours?.toFixed(2)}h). Blocked by practice setting.`
                                    : `Series ${seriesHours.toFixed(2)}h exceeds remaining auth (${remainingHours?.toFixed(2)}h). Proceed only if you plan to extend the authorization.`}
                            </Alert>
                        )}
                        {conflictWarnings.map((msg, i) => (
                            <Alert key={`conflict-${i}`} variant="warning">{msg}</Alert>
                        ))}
                        {finalizedInRangeCount > 0 && (
                            <Alert variant="warning">
                                {finalizedInRangeCount === 1
                                    ? '1 finalized occurrence in this range will be left unchanged.'
                                    : `${finalizedInRangeCount} finalized occurrences in this range will be left unchanged.`}
                            </Alert>
                        )}
                    </div>
                )}

                <div className="col-span-2 mb-3 mt-1 pt-3 border-t border-border">
                    <span className="text-2xs font-bold uppercase tracking-wider text-text-subtle">Additional information</span>
                </div>

                <FormField label="Notes" error={errors.notes?.message} className="col-span-2">
                    {(id) => <Textarea id={id} rows={2} {...register('notes')} />}
                </FormField>

                <div className="col-span-2 mb-3.5 flex items-center">
                    <label className="flex items-center gap-2 text-sm text-text">
                        <input type="checkbox" {...register('isSupervised')} />
                        Supervised session
                    </label>
                </div>

                {isSupervised && (
                    <FormField
                        label="Supervising BCBA"
                        required
                        error={errors.supervisingBcbaId?.message}
                        className="col-span-2"
                    >
                        {(id) => (
                            <SelectField id={id} {...register('supervisingBcbaId')}>
                                <option value="">Select BCBA…</option>
                                {bcbas.map((u) => (
                                    <option key={u.id} value={u.id}>{u.first} {u.last}</option>
                                ))}
                            </SelectField>
                        )}
                    </FormField>
                )}

                {isCanceling && (
                    <FormField
                        label="Cancel reason"
                        required
                        error={errors.cancelReasonId?.message}
                        className="col-span-2"
                    >
                        {(id) => (
                            <SelectField id={id} {...register('cancelReasonId')}>
                                <option value="">Select reason…</option>
                                {(cancelReasons.data ?? []).map((r) => (
                                    <option key={r.id} value={r.id}>{r.text}</option>
                                ))}
                            </SelectField>
                        )}
                    </FormField>
                )}
            </div>

            {serverError && (
                <div className="mt-2">
                    <Alert variant="danger">{serverError}</Alert>
                </div>
            )}

            </div>

            <div className="shrink-0 px-5 py-3 flex items-center gap-2 border-t border-border">
                {canDelete && !confirmDelete && (
                    <Button
                        type="button"
                        variant="danger"
                        className="mr-auto"
                        onClick={() => setConfirmDelete(true)}
                    >
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                )}
                {confirmDelete && (
                    <>
                        <span className="mr-auto text-xs text-text-muted">
                            {isSeries && scope === 'following'
                                ? "Delete every occurrence from this date onward? This can't be undone."
                                : "Delete this session? This can't be undone."}
                        </span>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setConfirmDelete(false)}
                            disabled={deletePending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="danger"
                            onClick={onDelete}
                            disabled={deletePending}
                        >
                            {deletePending ? 'Deleting…' : 'Delete'}
                        </Button>
                    </>
                )}
                {!confirmDelete && (
                    <>
                        <Button type="button" variant="ghost" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="success"
                            disabled={isSubmitting || hardBlock || (isPromoting && occurrences.length === 0)}
                        >
                            {isSubmitting ? 'Saving…' : 'Save'}
                        </Button>
                    </>
                )}
            </div>
        </form>
    );
}
